// ============================================================================
// tenantReadiness.js — PURE tenant config-readiness derivation.
// ----------------------------------------------------------------------------
// Decides whether a tenant has the MINIMUM operational setup to be marked Live:
//   1. at least one ACTIVE user
//   2. at least one ENABLED specialty
//   3. at least one ACTIVE Pack (any specialty; diagnostic/service packs count)
// Missing any of these is CRITICAL → blocks Live. Default/SLA impact settings
// are ADVISORY → never block.
//
// PURE: no Firestore, no React, no side effects — node-testable in isolation.
// (The platform health dashboard uses RISK/RISK_LABELS for at-a-glance signals;
// this module carries action-oriented checklist copy for the onboarding gate.)
// ============================================================================

export const TENANT_READINESS_CODE = {
  NO_USERS: 'NO_USERS',
  NO_SPECIALTIES: 'NO_SPECIALTIES',
  NO_PACKS: 'NO_PACKS',
  DEFAULT_SETTINGS: 'DEFAULT_SETTINGS',
  MISSING_SLA: 'MISSING_SLA',
};

export const TENANT_READINESS_LABEL = {
  NO_USERS:         'Add at least one active user.',
  NO_SPECIALTIES:   'Enable at least one receiving specialty.',
  NO_PACKS:         'Publish at least one active Pack.',
  DEFAULT_SETTINGS: 'Configure impact settings (still using defaults).',
  MISSING_SLA:      'Set SLA thresholds.',
};

/**
 * Derive tenant config-readiness from never-PHI counts.
 * @param {{ enabledSpecialtyCount?: number, activeUserCount?: number,
 *           activePackCount?: number, isDefaultSettings?: boolean, hasSla?: boolean }} counts
 * @returns {{ enabledSpecialtyCount: number, activeUserCount: number,
 *             activePackCount: number, criticalMissing: Array<{code,label}>,
 *             advisory: Array<{code,label}>, canGoLive: boolean }}
 */
export const deriveTenantConfigReadiness = ({
  enabledSpecialtyCount = 0,
  activeUserCount = 0,
  activePackCount = 0,
  isDefaultSettings = false,
  hasSla = true,
} = {}) => {
  const criticalMissing = [];
  if (activeUserCount === 0) {
    criticalMissing.push({ code: TENANT_READINESS_CODE.NO_USERS, label: TENANT_READINESS_LABEL.NO_USERS });
  }
  if (enabledSpecialtyCount === 0) {
    criticalMissing.push({ code: TENANT_READINESS_CODE.NO_SPECIALTIES, label: TENANT_READINESS_LABEL.NO_SPECIALTIES });
  }
  if (activePackCount === 0) {
    criticalMissing.push({ code: TENANT_READINESS_CODE.NO_PACKS, label: TENANT_READINESS_LABEL.NO_PACKS });
  }

  const advisory = [];
  if (isDefaultSettings) {
    advisory.push({ code: TENANT_READINESS_CODE.DEFAULT_SETTINGS, label: TENANT_READINESS_LABEL.DEFAULT_SETTINGS });
  }
  // Mirror platformHealth: MISSING_SLA only flagged when settings are NOT default
  // (a default-settings tenant already carries the DEFAULT_SETTINGS advisory).
  if (!hasSla && !isDefaultSettings) {
    advisory.push({ code: TENANT_READINESS_CODE.MISSING_SLA, label: TENANT_READINESS_LABEL.MISSING_SLA });
  }

  return {
    enabledSpecialtyCount,
    activeUserCount,
    activePackCount,
    criticalMissing,
    advisory,
    canGoLive: criticalMissing.length === 0,
  };
};

export default { TENANT_READINESS_CODE, TENANT_READINESS_LABEL, deriveTenantConfigReadiness };
